// src/context/brandVoice/widgets/StrListEditor.jsx — the `str_list` editor: a FLAT list of
// sentences (style_examples.prefer / .avoid, messaging.key_messages / .key_differentiators).
//
// ⭐ One box per entry, add / remove, and a WHOLE new array handed up on every change
// (immutable). ⛔ Never split: an entry may contain a colon ("Il digitale: una leva, non una
// vetrina" is ONE entry — sheet 08, and a legacy "Nome: descrizione" differentiator stays
// whole): the colon is `pair_list`'s business only. ⛔ It validates nothing; a blank entry is dropped by
// `wireShape.toWire` before anything is compared or sent (decision 5, 2026-09-28).
// ⛔ No reordering (decision 3). No real line breaks (ui/GrowingTextarea.jsx).
// ⭐ `lang` ('it' | 'en'): the bilingual wrapper (ui/BilingualColumns.jsx) mounts this editor
// TWICE for a bucket-D field, so the English copy's accessible labels carry a suffix and
// the two columns never share a name.
// ⭐ v200: the placeholder names the entry and gives an example, per field (`fieldPlaceholder.js`), and the box grows
// to fit it while empty.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import GrowingTextarea, { blockEnter, oneLine } from '../ui/GrowingTextarea.jsx';
import { ADD, EMPTY, INPUT, REMOVE, ROW } from './listStyles.js';
import { fieldPlaceholder } from './fieldPlaceholder.js';
import { FONT_SANS } from '../../../constants/theme.js';

export default function StrListEditor({ field, value, onChange, disabled = false, problems = [], lang = 'it' }) {
  const { t } = useTranslation('context');
  const items = Array.isArray(value) ? value : [];
  const suffix = lang === 'en' ? t('brandVoice.list.enSuffix') : '';
  const placeholder = fieldPlaceholder(t, {
    path: field?.field_path, fallbackKey: 'brandVoice.list.placeholder', lang,
  });
  const badRows = new Set((problems || []).map((p) => p?.params?.row).filter(Boolean));
  const replace = (index, text) => onChange?.(items.map((v, i) => (i === index ? text : v)));
  const remove = (index) => onChange?.(items.filter((_, i) => i !== index));
  const add = () => onChange?.([...items, '']);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontFamily: FONT_SANS }}>
      {items.length === 0 && <p style={EMPTY}>{t('brandVoice.list.empty')}</p>}
      {items.map((item, i) => {
        const n = i + 1;
        const invalid = badRows.has(n);
        return (
          // No reordering, so the index is a stable key for the life of a row.
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} style={ROW}>
            <GrowingTextarea
              aria-label={`${t('brandVoice.list.itemLabel', { row: n })}${suffix}`}
              aria-invalid={invalid || undefined}
              placeholder={placeholder}
              fitPlaceholder
              value={typeof item === 'string' ? item : ''}
              disabled={disabled}
              onKeyDown={blockEnter}
              onChange={(e) => replace(i, oneLine(e.target.value))}
              style={{ ...INPUT(invalid), flex: '1 1 240px', minWidth: 0 }}
            />
            <button
              type="button"
              aria-label={`${t('brandVoice.list.remove', { row: n })}${suffix}`}
              disabled={disabled}
              onClick={() => remove(i)}
              style={REMOVE(disabled)}
            >
              <X size={16} strokeWidth={1.8} aria-hidden="true" />
            </button>
          </div>
        );
      })}
      <div>
        <button
          type="button"
          aria-label={suffix ? `${t('brandVoice.list.add')}${suffix}` : undefined}
          disabled={disabled}
          onClick={add}
          style={ADD(disabled)}
        >
          {t('brandVoice.list.add')}
        </button>
      </div>
    </div>
  );
}
